import { useEffect, useId, useRef, useState, type FormEvent } from 'react'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import CheckIcon from '@mui/icons-material/Check'
import Link from 'src/components/Link'
import GoogleRecaptchaInfos from 'src/components/GoogleRecaptchaInfos'
import config from 'src/config'
import { getNewsletterStorefront } from 'src/functions/newsletterStorefront'
import getRecaptchaToken from 'src/functions/getRecaptchaToken'
import { NEWSLETTER_CONSENT_TEXT } from 'src/shared/newsletter'
import { getNewsletterAvailability, newsletterRequest } from './api'
import styles from './HomeNewsletter.module.css'

export default function HomeNewsletter() {
  const storefront = getNewsletterStorefront(config.shop?.shop)
  return storefront ? <HomeNewsletterContent storefront={storefront} /> : null
}

function HomeNewsletterContent({ storefront }: { storefront: NonNullable<ReturnType<typeof getNewsletterStorefront>> }) {
  const id = useId()
  const [availability, setAvailability] = useState<'loading' | 'available' | 'soon' | 'error'>('loading')
  const [retry, setRetry] = useState(0)
  const [email, setEmail] = useState('')
  const [website, setWebsite] = useState('')
  const [consent, setConsent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState('')
  const alive = useRef(true)
  const submitting = useRef(false)
  const submission = useRef<AbortController | null>(null)

  useEffect(() => {
    alive.current = true
    return () => { alive.current = false; submission.current?.abort() }
  }, [])
  useEffect(() => {
    const controller = new AbortController()
    setAvailability('loading')
    getNewsletterAvailability(controller.signal)
      .then(available => { if (!controller.signal.aborted) setAvailability(available ? 'available' : 'soon') })
      .catch(() => { if (!controller.signal.aborted) setAvailability('error') })
    return () => controller.abort()
  }, [retry])

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (submitting.current || !consent || availability !== 'available' || !event.currentTarget.checkValidity()) return
    submitting.current = true
    setBusy(true); setError('')
    const controller = new AbortController()
    submission.current = controller
    try {
      const recaptchaToken = await getRecaptchaToken('newsletter')
      if (!alive.current) return
      if (!recaptchaToken) throw new Error('Die Anmeldung konnte nicht geprüft werden. Bitte versuche es erneut.')
      await newsletterRequest('subscribe', { email: email.trim(), interests: [], frequency: 'weekly', consent: true,
        recaptchaToken, website, source: 'homepage' }, controller.signal)
      if (alive.current) { setSent(true); setEmail(''); setConsent(false) }
    } catch (reason) {
      if (alive.current) setError(reason instanceof Error ? reason.message : 'Die Anmeldung hat gerade nicht geklappt. Bitte versuche es erneut.')
    } finally {
      submitting.current = false
      if (alive.current) setBusy(false)
    }
  }

  return <section className={styles.home} style={storefront.style} aria-labelledby='home-newsletter-title' data-home-newsletter>
    <div className={styles.inner}>
      <div className={styles.copy}>
        <span className={styles.mailIcon} aria-hidden='true'><MailOutlineIcon /></span>
        <p className={styles.eyebrow}>{storefront.name} Lieblingspost</p>
        <h2 id='home-newsletter-title'>Frische Wohnideen<br />für dein Postfach.</h2>
        <p className={styles.intro}>Ein schönes Zuhause steckt voller Ideen. Wir schicken dir neue Lieblingsstücke, kleine Inspirationen und ausgewählte Angebote.</p>
        <ul className={styles.benefits}>
          <li><CheckIcon aria-hidden='true' /> Neuheiten und Wohnideen entdecken</li>
          <li><CheckIcon aria-hidden='true' /> Höchstens einmal pro Woche</li>
          <li><CheckIcon aria-hidden='true' /> Kostenlos und jederzeit abbestellbar</li>
        </ul>
      </div>

      <div className={styles.signup} aria-busy={busy || availability === 'loading'}>
        {sent ? <div className={styles.success} role='status'>
          <MailOutlineIcon aria-hidden='true' />
          <h3>Bitte prüfe dein Postfach.</h3>
          <p>Falls eine Bestätigung erforderlich ist, erhältst du eine E-Mail mit dem Bestätigungslink. Erst danach ist deine Anmeldung abgeschlossen.</p>
          <p>Schau bitte auch in deinen Spam-Ordner.</p>
        </div> : <>
          <h3>Deine nächste Lieblingsidee wartet.</h3>
          {availability === 'loading' && <p role='status' className={styles.hint}>Anmeldung wird geladen …</p>}
          {availability === 'soon' && <div role='status'><p>Unser Newsletter startet bald.</p><p className={styles.hint}>Die Anmeldung ist noch nicht geöffnet. Schau bald wieder vorbei.</p></div>}
          {availability === 'error' && <div><p role='alert' className={styles.hint}>Die Anmeldung ist gerade nicht verfügbar.</p><button type='button' className={styles.retry} onClick={() => setRetry(value => value + 1)}>Erneut laden</button></div>}
          {availability === 'available' && <form onSubmit={submit} aria-label='Newsletter auf der Startseite anmelden'>
            <label className={styles.label} htmlFor={`${id}-email`}>Deine E-Mail-Adresse</label>
            <div className={styles.emailRow}>
              <input id={`${id}-email`} type='email' name='email' autoComplete='email' required maxLength={254}
                value={email} disabled={busy} placeholder='name@beispiel.de' onChange={event => setEmail(event.target.value)} />
              <button type='submit' className={styles.submit} disabled={busy}>{busy ? 'Wird angemeldet …' : 'Jetzt anmelden'}</button>
            </div>
            <label className={styles.consent}>
              <input type='checkbox' required checked={consent} disabled={busy} onChange={event => setConsent(event.target.checked)} />
              <span>{NEWSLETTER_CONSENT_TEXT} <Link href='/datenschutzerklaerung'>Datenschutzhinweise</Link>.</span>
            </label>
            <div className={styles.trap} aria-hidden='true'><label htmlFor={`${id}-website`}>Website</label><input id={`${id}-website`} name='website' tabIndex={-1} autoComplete='off' value={website} onChange={event => setWebsite(event.target.value)} /></div>
            <GoogleRecaptchaInfos compact />
            {error && <p className={styles.error} role='alert'>{error}</p>}
            <p className={styles.hint}>Bitte bestätige deine Anmeldung anschließend per E-Mail.</p>
          </form>}
        </>}
        <div className={styles.links}>
          <Link href='/newsletter?source=homepage'>Themen & Häufigkeit selbst wählen</Link>
          <Link href='/newsletter?manage=1'>Newsletter verwalten oder abmelden</Link>
        </div>
      </div>
    </div>
  </section>
}
